import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Copy, MessageSquare, Archive, Trophy, X, Send, FileText, Check, Receipt, Bot } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import CommercialActivationKPIs from './CommercialActivationKPIs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  'new', 'reviewed', 'needs_customer_info', 'customer_contacted', 'customer_replied',
  'matched', 'provider_match_suggested', 'provider_match_approved',
  'sent_to_provider', 'provider_responded', 'quote_received', 'quote_sent_to_customer',
  'customer_accepted', 'customer_declined',
  'assignment_ready', 'assignment_sent_to_provider',
  'fee_invoice_pending', 'fee_invoice_sent',
  'won', 'lost', 'archived',
];

const STATUS_CLS = {
  new: 'bg-amber-100 text-amber-800',
  reviewed: 'bg-sky-100 text-sky-800',
  needs_customer_info: 'bg-orange-100 text-orange-800',
  customer_contacted: 'bg-teal-100 text-teal-800',
  customer_replied: 'bg-cyan-100 text-cyan-800',
  matched: 'bg-indigo-100 text-indigo-800',
  provider_match_suggested: 'bg-indigo-100 text-indigo-800',
  provider_match_approved: 'bg-purple-100 text-purple-800',
  sent_to_provider: 'bg-violet-100 text-violet-800',
  provider_responded: 'bg-blue-100 text-blue-800',
  quote_received: 'bg-cyan-100 text-cyan-800',
  quote_sent_to_customer: 'bg-teal-100 text-teal-800',
  customer_accepted: 'bg-emerald-100 text-emerald-800',
  customer_declined: 'bg-rose-100 text-rose-800',
  assignment_ready: 'bg-lime-100 text-lime-800',
  assignment_sent_to_provider: 'bg-green-100 text-green-800',
  fee_invoice_pending: 'bg-yellow-100 text-yellow-800',
  fee_invoice_sent: 'bg-blue-100 text-blue-800',
  won: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-rose-100 text-rose-800',
  archived: 'bg-slate-100 text-slate-700',
};

export default function QuoteRequestsTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', component_type: '', segment: '' });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [nextStatus, setNextStatus] = useState('');
  const [agentTasks, setAgentTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [acceptance, setAcceptance] = useState({ source: 'phone', note: '' });
  const [invDraft, setInvDraft] = useState({ fee_type: 'fixed_fee', fee_amount_ex_vat: '', vat_rate: 0.21, provider_billing_email: '', provider_company_name: '', provider_vat_number: '', notes: '' });
  const [researchSuggestion, setResearchSuggestion] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.component_type) params.component_type = filters.component_type;
      if (filters.segment) params.segment = filters.segment;
      const r = await axios.get(`${API}/admin/quote-requests`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setItems(r.data.quote_requests || []);
    } catch {
      toast.error('Kon offerteaanvragen niet laden');
    } finally { setLoading(false); }
  }, [token, filters.status, filters.component_type, filters.segment]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const by = {};
    STATUSES.forEach(s => { by[s] = 0; });
    items.forEach(q => {
      const s = q.status || q.request_status || 'new';
      by[s] = (by[s] || 0) + 1;
    });
    return by;
  }, [items]);

  const copyClient = (q) => {
    const lines = [
      q.name, q.email, q.phone,
      q.street && `${q.street} ${q.house_number || ''}`.trim(),
      [q.postcode, q.city].filter(Boolean).join(' '),
    ].filter(Boolean);
    navigator.clipboard?.writeText(lines.join('\n')).then(() => toast.success('Klantgegevens gekopieerd'));
  };

  const updateStatus = async (q, status, noteText = '') => {
    try {
      await axios.post(`${API}/admin/quote-requests/${q.request_id}/status`,
        { status, note: noteText }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status → ${status}`);
      load();
      setSelected(null);
      setNote(''); setNextStatus('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status update mislukt');
    }
  };

  // Phase C.1 — load agent tasks + invoices when a quote is opened
  const loadDetailExtras = useCallback(async (q) => {
    if (!q) return;
    try {
      const [tRes, iRes] = await Promise.all([
        axios.get(`${API}/admin/quote-requests/${q.request_id}/agent-tasks`, { headers: authH }),
        axios.get(`${API}/admin/fee-invoices`, { headers: authH, params: { quote_request_id: q.request_id } }),
      ]);
      setAgentTasks(tRes.data.tasks || []);
      setInvoices(iRes.data.invoices || []);
    } catch (e) {
      // silent — detail panels just stay empty
    }
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDetailExtras(selected); }, [selected, loadDetailExtras]);
  useEffect(() => { setResearchSuggestion(null); }, [selected?.request_id]);

  const suggestFromResearchPool = async () => {
    if (!selected) return;
    setResearchLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/quote-requests/${selected.request_id}/suggest-from-research-pool`,
        {},
        { headers: authH, params: { limit: 3, replace: false } },
      );
      setResearchSuggestion(r.data);
      toast.success(`${r.data.candidates_attached} research-providers voorgesteld (${r.data.candidates_found} gevonden)`);
      // refresh selected quote to show the new provider_candidates
      const list = await axios.get(`${API}/admin/quote-requests`, { headers: authH });
      const updated = (list.data.quote_requests || []).find(x => x.request_id === selected.request_id);
      if (updated) setSelected(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || 'Research-pool suggestie mislukt');
    } finally {
      setResearchLoading(false);
    }
  };

  const approveProvider = async (provider_id) => {
    try {
      await axios.post(`${API}/admin/quote-requests/${selected.request_id}/approve-provider`, { provider_id }, { headers: authH });
      toast.success(`Provider ${provider_id} approved`);
      load();
      // refresh selected
      const r = await axios.get(`${API}/admin/quote-requests`, { headers: authH });
      const updated = (r.data.quote_requests || []).find(x => x.request_id === selected.request_id);
      if (updated) setSelected(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve mislukt');
    }
  };

  const recordAcceptance = async (accepted) => {
    if (acceptance.source === 'manual' && !acceptance.note.trim()) {
      toast.error('Handmatige acceptatie vereist een notitie');
      return;
    }
    try {
      await axios.post(`${API}/admin/quote-requests/${selected.request_id}/customer-acceptance`,
        { accepted, source: acceptance.source, note: acceptance.note }, { headers: authH });
      toast.success(accepted ? 'Klantakkoord geregistreerd' : 'Klant declined');
      load();
      const r = await axios.get(`${API}/admin/quote-requests`, { headers: authH });
      const updated = (r.data.quote_requests || []).find(x => x.request_id === selected.request_id);
      if (updated) setSelected(updated);
      setAcceptance({ source: 'phone', note: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Acceptatie mislukt');
    }
  };

  const markAssignmentSent = async () => {
    try {
      await axios.post(`${API}/admin/quote-requests/${selected.request_id}/mark-assignment-sent`, {}, { headers: authH });
      toast.success('Assignment naar provider gemarkeerd verzonden');
      load();
      setSelected(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark sent mislukt');
    }
  };

  const createInvoice = async () => {
    try {
      const amt = parseFloat(invDraft.fee_amount_ex_vat);
      if (!(amt >= 0)) { toast.error('Bedrag ex btw is verplicht'); return; }
      await axios.post(`${API}/admin/fee-invoices`, {
        quote_request_id: selected.request_id,
        provider_id: selected.assigned_provider_id,
        component_type: selected.component_type || selected.category,
        ...invDraft,
        fee_amount_ex_vat: amt,
        vat_rate: parseFloat(invDraft.vat_rate),
        created_by: 'admin',
      }, { headers: authH });
      toast.success('Factuur draft aangemaakt');
      setInvDraft({ fee_type: 'fixed_fee', fee_amount_ex_vat: '', vat_rate: 0.21, provider_billing_email: '', provider_company_name: '', provider_vat_number: '', notes: '' });
      loadDetailExtras(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Factuur aanmaken mislukt');
    }
  };

  const invoiceAction = async (inv, action) => {
    try {
      await axios.post(`${API}/admin/fee-invoices/${inv.invoice_id}/${action}`, {}, { headers: authH });
      toast.success(`Invoice ${action} OK`);
      loadDetailExtras(selected);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} mislukt`);
    }
  };

  return (
    <div className="space-y-4" data-testid="quote-requests-tab">
      <CommercialActivationKPIs token={token} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-medium flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Offerteaanvragen</h2>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-muted" data-testid="quote-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {STATUSES.map(s => (
          <div key={s} className={`border rounded p-2 ${STATUS_CLS[s] || ''}`}>
            {s}<div className="text-lg font-medium">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="border rounded p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="quote-filters">
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Alle status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="component_type" value={filters.component_type} onChange={e => setFilters(f => ({ ...f, component_type: e.target.value }))} className="border rounded px-2 py-1" />
        <select value={filters.segment} onChange={e => setFilters(f => ({ ...f, segment: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Alle segments</option>
          <option value="residential">residential</option>
          <option value="mkb">mkb</option>
          <option value="vve">vve</option>
        </select>
      </div>

      <div className="border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="p-2">Klant</th>
              <th className="p-2">Adres</th>
              <th className="p-2">Categorie</th>
              <th className="p-2">Status</th>
              <th className="p-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Geen offerteaanvragen.</td></tr>}
            {items.map(q => {
              const status = q.status || q.request_status || 'new';
              return (
                <tr key={q.request_id} className="border-t hover:bg-muted/50" data-testid={`quote-row-${q.request_id}`}>
                  <td className="p-2">
                    <div className="font-medium">{q.name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{q.email} {q.phone && `· ${q.phone}`}</div>
                  </td>
                  <td className="p-2 text-xs">
                    {q.street && <>{q.street} {q.house_number}<br /></>}
                    {q.postcode} {q.city}
                  </td>
                  <td className="p-2 text-xs">
                    <div>{q.component_type || q.category || '—'}</div>
                    {q.product_id && <div className="text-muted-foreground">{q.product_id}</div>}
                    {q.is_pilot && (
                      <div
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[10px] font-semibold"
                        data-testid={`quote-pilot-badge-${q.request_id}`}
                      >
                        Pilot · {q.pilot_type === 'mkb' ? 'MKB' : 'Particulier'}
                      </div>
                    )}
                  </td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${STATUS_CLS[status] || ''}`}>{status}</span></td>
                  <td className="p-2 text-xs whitespace-nowrap">
                    <button onClick={() => { setSelected(q); setNextStatus(status); }} className="text-primary hover:underline mr-2" data-testid={`quote-open-${q.request_id}`}>Open</button>
                    <button onClick={() => copyClient(q)} className="text-primary hover:underline mr-2" data-testid={`quote-copy-${q.request_id}`}><Copy className="w-3 h-3 inline" /> Kopieer</button>
                    {status !== 'won' && <button onClick={() => updateStatus(q, 'won')} className="text-emerald-700 hover:underline mr-2" data-testid={`quote-won-${q.request_id}`}><Trophy className="w-3 h-3 inline" /> Won</button>}
                    {status !== 'lost' && <button onClick={() => updateStatus(q, 'lost')} className="text-rose-700 hover:underline mr-2" data-testid={`quote-lost-${q.request_id}`}><X className="w-3 h-3 inline" /> Lost</button>}
                    {status !== 'archived' && <button onClick={() => updateStatus(q, 'archived')} className="text-slate-600 hover:underline" data-testid={`quote-archive-${q.request_id}`}><Archive className="w-3 h-3 inline" /> Archive</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name || 'Offerteaanvraag'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Email:</span> {selected.email}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selected.phone || '—'}</div>
                  <div className="col-span-2"><span className="text-muted-foreground">Adres:</span> {selected.street} {selected.house_number}, {selected.postcode} {selected.city}</div>
                  <div><span className="text-muted-foreground">Type:</span> {selected.component_type || selected.category || '—'}</div>
                  <div><span className="text-muted-foreground">Product:</span> {selected.product_id || '—'}</div>
                  <div><span className="text-muted-foreground">Driver:</span> {selected.driver_id || '—'}</div>
                  <div><span className="text-muted-foreground">Intake:</span> {selected.intake_id || '—'}</div>
                  <div><span className="text-muted-foreground">Status:</span> <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_CLS[selected.status || selected.request_status] || ''}`}>{selected.status || selected.request_status}</span></div>
                  <div><span className="text-muted-foreground">Provider:</span> {selected.assigned_provider_id || '—'}</div>
                </div>
                {selected.context && <div className="border rounded p-2 text-xs whitespace-pre-wrap bg-muted/30">{selected.context}</div>}
                {selected.admin_notes && (
                  <div className="border rounded p-2 text-xs whitespace-pre-wrap bg-amber-50">
                    <div className="text-muted-foreground mb-0.5">Admin notes</div>
                    {selected.admin_notes}
                  </div>
                )}

                {selected.component_type === 'emergency_power' && (
                  <Section title="Noodstroom (emergency_power)" icon={Bot}>
                    <EmergencyPowerAdminBlock req={selected} />
                  </Section>
                )}

                {/* A. Agentadvies */}
                <Section title="Agentadvies" icon={Bot}>
                  {agentTasks.length === 0 && <div className="text-xs text-muted-foreground">Geen openstaande agent-tasks.</div>}
                  {agentTasks.map(t => (
                    <div key={t.task_id} className="border rounded p-2 mb-2 text-xs bg-slate-50" data-testid={`agent-task-${t.task_id}`}>
                      <div className="flex justify-between mb-1"><span className="font-medium">{t.proposed_action}</span><span className={`px-1.5 py-0.5 rounded ${t.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-slate-200'}`}>{t.status}</span></div>
                      {t.missing_fields?.length > 0 && <div className="text-rose-700">Mist: {t.missing_fields.join(', ')}</div>}
                      {t.proposed_status && <div>Voorgestelde status: <strong>{t.proposed_status}</strong></div>}
                      {t.proposed_provider_id && <div>Voorgestelde provider: <strong>{t.proposed_provider_id}</strong></div>}
                      {t.proposed_email_subject && <div className="mt-1"><div className="text-muted-foreground">Concept e-mail:</div><div>{t.proposed_email_subject}</div></div>}
                      {t.status === 'pending' && (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => axios.post(`${API}/admin/quote-requests/${selected.request_id}/agent-tasks/${t.task_id}/approve`, {}, { headers: authH }).then(() => { toast.success('Approved'); loadDetailExtras(selected); })} className="px-2 py-1 text-xs bg-emerald-600 text-white rounded" data-testid={`agent-task-approve-${t.task_id}`}>Approve</button>
                          <button onClick={() => axios.post(`${API}/admin/quote-requests/${selected.request_id}/agent-tasks/${t.task_id}/reject`, {}, { headers: authH }).then(() => { toast.success('Rejected'); loadDetailExtras(selected); })} className="px-2 py-1 text-xs bg-rose-600 text-white rounded" data-testid={`agent-task-reject-${t.task_id}`}>Reject</button>
                        </div>
                      )}
                    </div>
                  ))}
                </Section>

                {/* B. Provider matching */}
                <Section title="Provider matching" icon={Send}>
                  <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
                    <button
                      onClick={suggestFromResearchPool}
                      disabled={researchLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                      data-testid="suggest-from-research-pool-btn"
                      title="Zoekt top-3 research_candidate providers op basis van category + postcode_prefix"
                    >
                      {researchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span aria-hidden>🔍</span>}
                      Vind 3 providers uit research-pool
                    </button>
                    {researchSuggestion && (
                      <span className="text-[11px] text-muted-foreground" data-testid="research-pool-stats">
                        cat={researchSuggestion.category} · regio={researchSuggestion.region_prefix} ·
                        gevonden={researchSuggestion.candidates_found} ·
                        toegevoegd={researchSuggestion.candidates_attached}
                      </span>
                    )}
                  </div>

                  {researchSuggestion && (
                    <div
                      className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                      data-testid="research-pool-warning"
                    >
                      <strong>Let op:</strong> Research providers zijn nog niet goedgekeurd en krijgen geen
                      klantgegevens totdat admin dit expliciet goedkeurt via approve-provider en
                      mark-assignment-sent.
                    </div>
                  )}

                  {researchSuggestion?.candidates?.length > 0 && (
                    <div className="mb-3 space-y-2" data-testid="research-pool-results">
                      <div className="text-[11px] uppercase text-muted-foreground">
                        Voorgesteld uit research-pool (status=suggested)
                      </div>
                      {researchSuggestion.candidates.map(c => (
                        <div
                          key={c.provider_id}
                          className="border rounded p-2 text-xs bg-slate-50"
                          data-testid={`research-pool-candidate-${c.provider_id}`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{c.company_name}</div>
                              <div className="text-muted-foreground truncate">
                                {c.city || '—'} · {(c.categories || []).join(',') || '—'} ·
                                regio {(c.postcode_prefixes || []).join(',') || '—'}
                              </div>
                              {c.specialization_notes && (
                                <div className="text-muted-foreground italic mt-0.5 truncate">
                                  "{c.specialization_notes}"
                                </div>
                              )}
                              <div className="mt-1 flex flex-wrap gap-1 items-center">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                  c.research_status === 'prepared' ? 'bg-emerald-100 text-emerald-800'
                                    : c.research_status === 'needs_review' ? 'bg-amber-100 text-amber-800'
                                    : c.research_status === 'missing_contact' ? 'bg-rose-100 text-rose-800'
                                    : 'bg-slate-200'}`}>
                                  {c.research_status || '—'}
                                </span>
                                {(c.missing_fields || []).length > 0 && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-rose-100 text-rose-800">
                                    mist: {c.missing_fields.join(', ')}
                                  </span>
                                )}
                                {c.risk_notes && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-800">
                                    risk: {c.risk_notes}
                                  </span>
                                )}
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-700">
                                  {(c.source_urls || []).length} bron(nen)
                                </span>
                              </div>
                              {(c.source_urls || []).length > 0 && (
                                <ul className="mt-1 ml-3 list-disc text-[10px] text-muted-foreground">
                                  {c.source_urls.slice(0, 2).map(u => (
                                    <li key={u} className="truncate"><a href={u} target="_blank" rel="noreferrer" className="underline">{u}</a></li>
                                  ))}
                                </ul>
                              )}
                              <div className="text-[10px] text-muted-foreground mt-1 italic">
                                {c.match_reason}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-100 text-indigo-800">
                                suggested
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                c.approved ? 'bg-rose-200 text-rose-900' : 'bg-emerald-100 text-emerald-800'}`}>
                                approved={String(!!c.approved)}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                c.visible ? 'bg-rose-200 text-rose-900' : 'bg-emerald-100 text-emerald-800'}`}>
                                visible={String(!!c.visible)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {researchSuggestion && researchSuggestion.candidates_attached === 0 && (
                    <div className="mb-2 text-xs text-muted-foreground" data-testid="research-pool-empty">
                      Geen geschikte research-providers gevonden voor deze category × regio.
                    </div>
                  )}

                  {(selected.provider_candidates || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground">Geen provider candidates.</div>
                  ) : (
                    <div className="space-y-2">
                      {selected.provider_candidates.map(c => (
                        <div key={c.provider_id} className="border rounded p-2 text-xs flex justify-between items-center" data-testid={`provider-candidate-${c.provider_id}`}>
                          <div>
                            <div className="font-medium">{c.company_name}</div>
                            <div className="text-muted-foreground">{c.provider_id} · {c.commission_type}: {c.commission_amount} · rating {c.rating}</div>
                            {c.agent_reason && <div className="text-muted-foreground italic mt-0.5">"{c.agent_reason}"</div>}
                          </div>
                          <div className="flex gap-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${c.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : c.status === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100'}`}>{c.status}</span>
                            {c.status !== 'approved' && (
                              <button onClick={() => approveProvider(c.provider_id)} className="px-2 py-0.5 text-xs bg-primary text-white rounded" data-testid={`approve-provider-${c.provider_id}`}>Approve</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {selected.assigned_provider_id && (
                    <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs">Toegewezen provider: <strong>{selected.assigned_provider_id}</strong></div>
                  )}
                </Section>

                {/* C. Klantakkoord */}
                <Section title="Klantakkoord" icon={Check}>
                  {selected.customer_accepted_at ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                      ✓ Akkoord geregistreerd op {new Date(selected.customer_accepted_at).toLocaleString()} via <strong>{selected.customer_acceptance_source}</strong>
                      {selected.customer_acceptance_note && <div className="mt-1 italic">"{selected.customer_acceptance_note}"</div>}
                    </div>
                  ) : (
                    <div className="space-y-2 text-xs">
                      <div className="flex gap-2 items-center">
                        <select value={acceptance.source} onChange={e => setAcceptance(a => ({ ...a, source: e.target.value }))} className="border rounded px-2 py-1" data-testid="acceptance-source">
                          <option value="email">email</option>
                          <option value="phone">phone</option>
                          <option value="form">form</option>
                          <option value="manual">manual</option>
                        </select>
                        <input value={acceptance.note} onChange={e => setAcceptance(a => ({ ...a, note: e.target.value }))} placeholder={acceptance.source === 'manual' ? 'Notitie verplicht' : 'Notitie (optioneel)'} className="border rounded px-2 py-1 flex-1" data-testid="acceptance-note" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => recordAcceptance(true)} className="px-3 py-1 bg-emerald-600 text-white rounded text-xs" data-testid="acceptance-accept">Klant accepteerde</button>
                        <button onClick={() => recordAcceptance(false)} className="px-3 py-1 bg-rose-600 text-white rounded text-xs" data-testid="acceptance-decline">Klant declined</button>
                      </div>
                    </div>
                  )}
                </Section>

                {/* D. Opdracht naar provider */}
                <Section title="Opdracht naar provider" icon={Send}>
                  {(selected.status === 'customer_accepted' || selected.status === 'assignment_ready') && selected.assigned_provider_id ? (
                    <button onClick={markAssignmentSent} className="px-3 py-1.5 bg-primary text-white rounded text-xs" data-testid="mark-assignment-sent">
                      Markeer opdracht verzonden → assignment_sent_to_provider
                    </button>
                  ) : selected.status === 'assignment_sent_to_provider' ? (
                    <div className="bg-green-50 border border-green-200 rounded p-2 text-xs">✓ Opdracht verzonden op {selected.assignment_sent_at && new Date(selected.assignment_sent_at).toLocaleString()}</div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Beschikbaar zodra status=customer_accepted en provider is toegewezen.</div>
                  )}
                </Section>

                {/* E. Fee invoice */}
                <Section title="Fee invoice (incl. btw)" icon={Receipt}>
                  {/* Existing invoices */}
                  {invoices.map(inv => (
                    <div key={inv.invoice_id} className="border rounded p-2 mb-2 text-xs bg-slate-50" data-testid={`invoice-${inv.invoice_id}`}>
                      <div className="flex justify-between mb-1">
                        <span className="font-medium">{inv.invoice_number || `(draft) ${inv.fee_type}`}</span>
                        <span className={`px-1.5 py-0.5 rounded ${inv.invoice_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : inv.invoice_status === 'sent' ? 'bg-blue-100 text-blue-800' : inv.invoice_status === 'approved' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'}`}>{inv.invoice_status}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><span className="text-muted-foreground">Ex btw:</span> €{inv.fee_amount_ex_vat?.toFixed(2)}</div>
                        <div><span className="text-muted-foreground">Btw ({(inv.vat_rate * 100).toFixed(0)}%):</span> €{inv.vat_amount?.toFixed(2)}</div>
                        <div><span className="text-muted-foreground">Incl btw:</span> <strong>€{inv.fee_amount_inc_vat?.toFixed(2)}</strong></div>
                      </div>
                      <div className="text-muted-foreground mt-1">{inv.invoice_text}</div>
                      <div className="flex gap-1 mt-2">
                        {(inv.invoice_status === 'draft' || inv.invoice_status === 'pending_approval') && <button onClick={() => invoiceAction(inv, 'approve')} className="px-2 py-0.5 text-xs bg-emerald-600 text-white rounded" data-testid={`invoice-approve-${inv.invoice_id}`}>Approve</button>}
                        {inv.invoice_status === 'approved' && <button onClick={() => invoiceAction(inv, 'mark-sent')} className="px-2 py-0.5 text-xs bg-primary text-white rounded" data-testid={`invoice-sent-${inv.invoice_id}`}>Mark sent</button>}
                        {(inv.invoice_status === 'sent' || inv.invoice_status === 'approved') && <button onClick={() => invoiceAction(inv, 'mark-paid')} className="px-2 py-0.5 text-xs bg-emerald-600 text-white rounded" data-testid={`invoice-paid-${inv.invoice_id}`}>Mark paid</button>}
                      </div>
                    </div>
                  ))}
                  {/* Draft form */}
                  <div className="border-t pt-2 mt-2 text-xs">
                    <div className="text-muted-foreground mb-1">Nieuwe fee invoice (draft)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={invDraft.fee_type} onChange={e => setInvDraft(d => ({ ...d, fee_type: e.target.value }))} className="border rounded px-2 py-1" data-testid="invoice-fee-type">
                        <option value="fixed_fee">fixed_fee</option>
                        <option value="percentage">percentage</option>
                        <option value="lead_fee">lead_fee</option>
                        <option value="success_fee">success_fee</option>
                        <option value="manual">manual</option>
                      </select>
                      <input type="number" step="0.01" placeholder="Bedrag ex btw" value={invDraft.fee_amount_ex_vat} onChange={e => setInvDraft(d => ({ ...d, fee_amount_ex_vat: e.target.value }))} className="border rounded px-2 py-1" data-testid="invoice-amount" />
                      <input type="number" step="0.01" placeholder="Btw % (0.21)" value={invDraft.vat_rate} onChange={e => setInvDraft(d => ({ ...d, vat_rate: e.target.value }))} className="border rounded px-2 py-1" />
                      <input placeholder="Provider billing email" value={invDraft.provider_billing_email} onChange={e => setInvDraft(d => ({ ...d, provider_billing_email: e.target.value }))} className="border rounded px-2 py-1" />
                      <input placeholder="Provider company name" value={invDraft.provider_company_name} onChange={e => setInvDraft(d => ({ ...d, provider_company_name: e.target.value }))} className="border rounded px-2 py-1 col-span-2" />
                      <input placeholder="Provider btw nummer" value={invDraft.provider_vat_number} onChange={e => setInvDraft(d => ({ ...d, provider_vat_number: e.target.value }))} className="border rounded px-2 py-1 col-span-2" />
                      <input placeholder="Notities" value={invDraft.notes} onChange={e => setInvDraft(d => ({ ...d, notes: e.target.value }))} className="border rounded px-2 py-1 col-span-2" />
                    </div>
                    {invDraft.fee_amount_ex_vat && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Preview: €{parseFloat(invDraft.fee_amount_ex_vat || 0).toFixed(2)} ex btw + €{(parseFloat(invDraft.fee_amount_ex_vat || 0) * parseFloat(invDraft.vat_rate || 0)).toFixed(2)} btw = <strong>€{(parseFloat(invDraft.fee_amount_ex_vat || 0) * (1 + parseFloat(invDraft.vat_rate || 0))).toFixed(2)}</strong> incl btw
                      </div>
                    )}
                    <button onClick={createInvoice} className="mt-2 px-3 py-1 bg-primary text-white rounded text-xs" data-testid="invoice-create">Draft aanmaken</button>
                  </div>
                </Section>

                <div className="border-t pt-3 space-y-2">
                  <label className="text-xs uppercase text-muted-foreground">Handmatige status-override</label>
                  <select value={nextStatus} onChange={e => setNextStatus(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" data-testid="quote-next-status">
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <label className="text-xs uppercase text-muted-foreground">Notitie</label>
                  <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full border rounded px-2 py-1 text-sm" data-testid="quote-note-input" />
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setSelected(null)} className="px-3 py-1.5 text-sm border rounded">Sluiten</button>
                <button onClick={() => updateStatus(selected, nextStatus, note)} disabled={!nextStatus} className="px-3 py-1.5 text-sm bg-primary text-white rounded disabled:opacity-50" data-testid="quote-save-status">Status opslaan</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border rounded p-3">
      <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}


const EP_GROUP_LABELS = {
  fridge: 'Koelkast', freezer: 'Vriezer', lights: 'Verlichting',
  router: 'Internet/router', phone_laptop: 'Telefoon/laptop',
  cooking_light: 'Koken (licht)', water_pump: 'Drinkwater',
  central_heating: 'CV/verwarmingspomp', heat_pump: 'Warmtepomp',
  induction_cooking: 'Inductiekoken', electric_boiler: 'Elektrische boiler',
  medical_equipment: 'Medische apparatuur', alarm_door: 'Alarm/poort/deur',
  workplace: 'Thuiswerkplek', other: 'Anders',
};
const EP_ROUTE_LABEL = {
  power_station: 'Power station (affiliate of advies)',
  generator: 'Aggregaat (affiliate of advies)',
  installer_check: 'Installateurcheck (offerte)',
  fixed_backup: 'Vaste backup (offerte)',
};

function EmergencyPowerAdminBlock({ req }) {
  const data = req.emergency_power_data || {};
  const advice = req.emergency_power_advice || {};
  const groups = data.selected_priority_groups || data.selected_groups || [];
  const route = advice.recommended_route || '—';
  const meterbox = advice.meterbox_module_required || data.meterbox_module_required || false;
  const generatorMeas = advice.generator_input_measurement_required || data.generator_input_measurement_required || false;
  const smartPlug = advice.smart_plug_suggestions || data.smart_plug_suggestions || false;
  const installerReq = advice.installer_required || advice.requires_installer_check || data.installer_required || false;
  const heavy = advice.heavy_load_warning || data.heavy_load_warning || false;
  const providerCats = advice.provider_category_suggestions || [];
  return (
    <div className="space-y-2 text-xs" data-testid="emergency-power-admin-block">
      <div className="flex flex-wrap gap-1.5" data-testid="emergency-power-admin-chips">
        {meterbox && (
          <span className="px-2 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-800 font-medium" data-testid="ep-chip-meterbox">
            Meterkast/omschakeling — installateur verplicht
          </span>
        )}
        {smartPlug && (
          <span className="px-2 py-0.5 rounded-full bg-sky-100 border border-sky-200 text-sky-800 font-medium" data-testid="ep-chip-smart-plug">
            Slimme stekkeradvies voor losse apparaten
          </span>
        )}
        {heavy && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-900 font-medium" data-testid="ep-chip-heavy-load">
            Heavy load warning
          </span>
        )}
        {generatorMeas && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-900 font-medium" data-testid="ep-chip-generator-input">
            Generator-input meting nodig
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><span className="text-muted-foreground">Scenario:</span> {data.scenario || '—'}</div>
        <div><span className="text-muted-foreground">Overbruggingsduur:</span> {data.duration_target || data.emergency_duration_target || '—'}</div>
        <div><span className="text-muted-foreground">Gas aanwezig:</span> {data.has_gas || '—'}</div>
        <div><span className="text-muted-foreground">Voorkeur:</span> {data.solution_preference || '—'}</div>
        <div><span className="text-muted-foreground">Load profiel:</span> {advice.load_profile || '—'}</div>
        <div><span className="text-muted-foreground">Aanbevolen route:</span> <strong>{EP_ROUTE_LABEL[route] || route}</strong></div>
        <div><span className="text-muted-foreground">Installateur vereist:</span> {installerReq ? 'Ja' : 'Nee'}</div>
        <div><span className="text-muted-foreground">Warmtepomp:</span> {(advice.heat_pump_selected || data.heat_pump_selected) ? 'Ja' : 'Nee'}</div>
      </div>
      {groups.length > 0 && (
        <div data-testid="emergency-power-admin-groups">
          <div className="text-muted-foreground mb-1">Prioritaire groepen ({groups.length})</div>
          <div className="flex flex-wrap gap-1">
            {groups.map(g => (
              <span key={g} className="px-1.5 py-0.5 rounded bg-slate-100 border">{EP_GROUP_LABELS[g] || g}</span>
            ))}
          </div>
        </div>
      )}
      {providerCats.length > 0 && (
        <div data-testid="emergency-power-admin-providers">
          <div className="text-muted-foreground mb-1">Provider-categorieën (advies)</div>
          <div className="flex flex-wrap gap-1">
            {providerCats.map(c => (
              <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-900">{c}</span>
            ))}
          </div>
        </div>
      )}
      {advice.agent_summary && (
        <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1" data-testid="emergency-power-admin-agent-summary">
          <span className="text-muted-foreground">Agent samenvatting:</span> {advice.agent_summary}
        </div>
      )}
      {installerReq && (
        <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-rose-900" data-testid="emergency-power-admin-installer-required">
          <strong>Installateurcheck verplicht.</strong> Deze aanvraag vraagt installateurcheck omdat woninggroepen/noodbedrijf zijn geselecteerd.
        </div>
      )}
      {(advice.warnings || []).length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900" data-testid="emergency-power-admin-warnings">
          <div className="font-semibold mb-1">Waarschuwingen</div>
          <ul className="list-disc list-inside space-y-0.5">
            {advice.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
